import { useCallback, useEffect, useState } from 'react';

/** Minimal shape of the album generate response fields we react to. */
interface AlbumGenerateResultLike {
  duplicate_exists?: boolean;
  generated_at?: string;
}

/**
 * Shared duplicate-album prompt state.
 *
 * When `generateAlbum` returns `duplicate_exists`, the server did NOT start a
 * new job — an identical album already exists. Both the single-column panel and
 * the two-column inspector must surface the "already exists" confirmation, so
 * this hook centralizes the detection + open/generatedAt state instead of each
 * host reimplementing it (which is why the two-column path used to silently do
 * nothing on Generate).
 */
export function useAlbumDuplicatePrompt(
  lastGenerateResult: AlbumGenerateResultLike | undefined,
  resetGenerateResult: () => void
) {
  const [open, setOpen] = useState(false);
  const [generatedAt, setGeneratedAt] = useState<string | undefined>();

  useEffect(() => {
    if (lastGenerateResult?.duplicate_exists && !open) {
      setGeneratedAt(lastGenerateResult.generated_at);
      setOpen(true);
    }
    // Only react to a fresh result, not to `open` toggling.
  }, [lastGenerateResult]);

  // Close and clear the mutation result so a later identical Generate can
  // re-trigger the prompt (the mutation would otherwise keep the stale data).
  const dismiss = useCallback(() => {
    setOpen(false);
    setGeneratedAt(undefined);
    resetGenerateResult();
  }, [resetGenerateResult]);

  return { open, generatedAt, setOpen, dismiss };
}
